<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <h1 class="title">Safari cars</h1>
      <app-cells>
        <h2 class="title">Rental Cars</h2>
        <app-button @click.native="$modal.show('other-modal')" size="small">Add car</app-button>
      </app-cells>
      <app-cells>
        <app-filters
          v-model="active_filter"
          :filters="active_filters"
          name="filterStatus"
          @change="fetchOtherCarsItems"
        />
        <app-select
          @input="fetchOtherCarsItems"
          v-model="filter_rental_company"
          :options="company_options"
          :clearable="true"
          placeholder="Rental company"
          class="select--200"
        />
      </app-cells>
      <v-client-table
        :data="table_other.items"
        :columns="table_other.columns"
        :options="table_other.options"
        class="table-default table-default--small-cell"
      >
        <template #h__rental_company><div class="table-default__left">Company</div></template>
        <template #h__car><div class="table-default__left">Car</div></template>
        <template #rental_company="{ row }">
          <div class="table-default__left">
            {{ row.rental_company.name }}
          </div>
        </template>
        <template #car="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'safari-cars-detail', params: { id: row.id } }"
              class="table-default__link-border table-default__link-border--inline"
            >
              {{ row.plate_number }}
            </router-link>
          </div>
        </template>
        <template #seats="{ row }">
          {{ row.seats || '-' }}
        </template>
        <template #year="{ row }">
          {{ row.year }}
        </template>
        <template #car_type="{ row }">
          <span v-if="row.car_type" class="status-title" :style="{ 'backgroundColor': row.car_type.color }">
            {{ row.car_type.name }}
          </span>
          <template v-else>-</template>
        </template>
        <template #hot_pots="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.hot_pots ? '#77b017' : '#e85c20' }">
            {{ row.hot_pots ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #front_a_c="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.front_a_c ? '#77b017' : '#e85c20' }">
            {{ row.front_a_c ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #rear_a_c="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.rear_a_c ? '#77b017' : '#e85c20' }">
            {{ row.rear_a_c ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #fridge="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.fridge ? '#77b017' : '#e85c20' }">
            {{ row.fridge ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #wifi="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.wifi ? '#77b017' : '#e85c20' }"
            v-tooltip="{ content: wifiInfo(row.ssid, row.password), placement: 'top-start', offset: 10, html: true }"
          >
            {{ row.wifi ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #binocular="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.binocular > 0 ? '#77b017' : '#e85c20' }">
            {{ row.binocular }}
          </span>
        </template>
        <template #table="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.table > 0 ? '#77b017' : '#e85c20' }">
            {{ row.table }}
          </span>
        </template>
        <template #chairs="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.chairs > 0 ? '#77b017' : '#e85c20' }">
            {{ row.chairs }}
          </span>
        </template>
        <template #seats_movable="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.seats_movable ? '#77b017' : '#e85c20' }">
            {{ row.seats_movable ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #umbrellas="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.umbrellas > 0 ? '#77b017' : '#e85c20' }">
            {{ row.umbrellas }}
          </span>
        </template>
        <template #notes="{ row }">
          <div class="table-default__controls">
            <div
              v-tooltip="{ content: row.notes, placement: 'top-start', offset: 10 }"
              class="table-default__activity table-default__activity--notes"
              :class="{'table-default__activity--active': row.notes}"
            ></div>
          </div>
        </template>
      </v-client-table>
      <app-cells>
        <h2 class="title">Safari drivers</h2>
      </app-cells>
      <app-cells>
        <h3 class="title">Altezza</h3>
      </app-cells>
      <v-client-table
        :data="table_altezza.items"
        :columns="table_altezza.columns"
        :options="table_altezza.options"
        class="table-default"
      >
        <template #h__first_name><div class="table-default__left">Name</div></template>
        <template #h__company><div class="table-default__left">Company</div></template>
        <template #h__position><div class="table-default__left">Position</div></template>
        <template #first_name="{ row }">
          <div class="table-default__left table-default__left--photo">
            <div class="table-default__photo">
              <img :src="getUserAvatar(row.crop.crop_url, row.gender?.code || null)" alt="Avatar">
            </div>
            <router-link :to="{ name: 'employees-profile', params: { id: row.id } }" class="table-default__link-border">
              {{ row.first_name }} {{ row.last_name }}
            </router-link>
          </div>
        </template>
        <template #age="{ row }">
          <span v-if="row.age" v-tooltip="{ content: dateFormat(row.dob), placement: 'top-start', offset: 10 }">
            {{ row.age }}
          </span>
          <template v-else>-</template>
        </template>
        <template #communication="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.email, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.email)"
              class="table-default__activity table-default__activity--mail"
              :class="{'table-default__activity--active': row.email}"
            >
            </button>
            <button
              v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.phone)"
              class="table-default__activity table-default__activity--phone"
              :class="{'table-default__activity--active': row.phone}"
            >
            </button>
            <a
              :href="`https://t.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--tg"
              :class="{'table-default__activity--active': row.phone_telegram}"
            >
            </a>
            <a
              :href="`https://wa.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--wa"
              :class="{'table-default__activity--active': row.phone_whatsapp}"
            >
            </a>
          </div>
        </template>
        <template #national_id="{ row }">
          <div class="table-default__controls">
            <app-document-icon
              name="national_id"
              :fields="{ id: row.national_id, expire: row.national_id_expire, scan_copy: row.national_id_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon name="voting_card" :fields="{ id: row.voting_card }" />
            <app-document-icon
              name="driver_licence"
              :fields="{ id: row.driver_licence, expire: row.driver_licence_expire, scan_copy: row.driver_licence_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon
              name="tin"
              :fields="{ id: row.tin, scan_copy: row.tin_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon
              name="nssf_id"
              :fields="{ id: row.nssf_id, scan_copy: row.nssf_id_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon
              name="nhif_id"
              :fields="{ id: row.nhif_id, scan_copy: row.nhif_id_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
          </div>
        </template>
        <template #profile="{ row }">
          <div class="table-default__controls">
            <app-document-icon name="bank_account" :fields="{ id: row.bank_account, bank_name: row.bank }"/>
            <app-document-icon
              name="residence"
              :fields="{ id: row.residence }"
            />
            <app-document-icon
              name="guarantor_employee"
              :fields="{ id: row.guarantor_employee }"
            />
            <app-document-icon
              name="guarantor"
              :fields="{ id: row.guarantor }"
            />
          </div>
        </template>
        <template #warnings="{ row }">{{ row.warnings ? row.warnings.length : '-' }}</template>
      </v-client-table>
      <app-cells>
        <h3 class="title">Other</h3>
        <app-button @click.native="$modal.show('other_driver_popup')" size="small">Add driver</app-button>
      </app-cells>
      <app-cells>
        <app-filters
          v-model="other_filter"
          :filters="other_filters"
          name="other_filter"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table_other_drivers.items"
        :columns="table_other_drivers.columns"
        :options="table_other_drivers.options"
        class="table-default"
      >
        <template #h__first_name><div class="table-default__left">Name</div></template>
        <template #h__rental_company><div class="table-default__left">Company</div></template>
        <template #first_name="{ row }">
          <div class="table-default__left">
            <router-link :to="{ name: 'safari-drivers-detail', params: { id: row.id } }" class="table-default__link-border">
              {{ row.first_name }} {{ row.last_name }}
            </router-link>
          </div>
        </template>
        <template #rental_company="{ row }">
          <div class="table-default__left">
            <router-link v-if="row.rental_company" to="" class="table-default__link-border">
              {{ row.rental_company.name }}
            </router-link>
            <template v-else>-</template>
          </div>
        </template>
        <template #phone="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.phone)"
              class="table-default__activity table-default__activity--phone"
              :class="{'table-default__activity--active': row.phone}"
            >
            </button>
            <a
              :href="`https://t.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--tg"
              :class="{'table-default__activity--active': row.phone_telegram}"
            >
            </a>
            <a
              :href="`https://wa.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--wa"
              :class="{'table-default__activity--active': row.phone_whatsapp}"
            >
            </a>
          </div>
        </template>
        <template #additional_phone="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.additional_phone, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.additional_phone)"
              class="table-default__activity table-default__activity--phone"
              :class="{'table-default__activity--active': row.additional_phone}"
            >
            </button>
            <a
              :href="`https://t.me/${row.additional_phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--tg"
              :class="{'table-default__activity--active': row.additional_phone_telegram}"
            >
            </a>
            <a
              :href="`https://wa.me/${row.additional_phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--wa"
              :class="{'table-default__activity--active': row.additional_phone_whatsapp}"
            >
            </a>
          </div>
        </template>
        <template #op_manual_signed="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.op_manual_signed ? '#77b017' : '#e85c20' }"
          >
            {{ row.op_manual_signed ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #profile="{ row }">
          <div class="table-default__controls">
            <app-document-icon
              name="driver_licence"
              :fields="{ id: row.driver_licence, expire: row.driver_licence_expire, scan_copy: row.driver_licence_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
          </div>
        </template>
      </v-client-table>
      <modal
        name="other-modal"
        :width="700"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
      >
        <div class="popup">
          <button @click="$modal.hide('other-modal')" class="popup__close" type="button"></button>
          <div class="popup__title">
            <span>Adding other safari car</span>
          </div>
          <div class="popup__body">
            <form @submit.prevent="onCheckOtherForm">
              <app-grid>
                <template #item-1>
                  <app-form-group label="Company" required>
                    <app-select
                      v-model="other_form.rental_company"
                      :reduce="item => item.id"
                      :options="company_options"
                      :clearable="false"
                      placeholder="Company"
                      :error="$v.other_form.rental_company.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.rental_company.$dirty && !$v.other_form.rental_company.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Plate number" label-for="plate_number" required>
                    <app-input
                      v-model="other_form.plate_number"
                      placeholder="Plate number"
                      id="plate_number"
                      name="plate_number"
                      :error="$v.other_form.plate_number.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.plate_number.$dirty && !$v.other_form.plate_number.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Located" required>
                    <app-input
                      v-model="other_form.located"
                      placeholder="Located"
                      id="located"
                      name="located"
                      :error="$v.other_form.located.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.located.$dirty && !$v.other_form.located.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Seats" label-for="rental_car_seats" required>
                    <app-input
                      v-model.number="other_form.seats"
                      type="number"
                      placeholder="Seats"
                      id="rental_car_seats"
                      name="rental_car_seats"
                      :error="$v.other_form.seats.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.seats.$dirty && !$v.other_form.seats.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Year" label-for="rental_car_year" required>
                    <app-input
                      v-model.number="other_form.year"
                      type="number"
                      placeholder="Year"
                      id="rental_car_year"
                      name="rental_car_year"
                      :error="$v.other_form.year.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.year.$dirty && !$v.other_form.year.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Car type" required>
                    <app-select
                      v-model="other_form.car_type"
                      :reduce="item => item.code"
                      :options="car_type_options"
                      :clearable="false"
                      placeholder="Car level"
                      :error="$v.other_form.car_type.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.car_type.$dirty && !$v.other_form.car_type.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group v-if="other_form.wifi" label="SSID Name" label-for="ssid_name">
                    <app-input
                      v-model="other_form.ssid"
                      placeholder="SSID Name"
                      id="ssid_name"
                      name="ssid_name"
                    />
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Seats movable: <app-switcher v-model="other_form.seats_movable" /></app-cells>
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Medical kit: <app-switcher v-model="other_form.medical_kit" /></app-cells>
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Front a/c: <app-switcher v-model="other_form.front_a_c" /></app-cells>
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Rear a/c: <app-switcher v-model="other_form.rear_a_c" /></app-cells>
                  </app-form-group>
                </template>
                <template #item-2>
                  <app-form-group label="Binocular" label-for="binocular" required>
                    <app-input
                      v-model.number="other_form.binocular"
                      type="number"
                      placeholder="123"
                      id="binocular"
                      name="binocular"
                      :error="$v.other_form.binocular.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.binocular.$dirty && !$v.other_form.binocular.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Table" label-for="table_other" required>
                    <app-input
                      v-model.number="other_form.table"
                      type="number"
                      placeholder="123"
                      id="table_other"
                      name="table_other"
                      :error="$v.other_form.table.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.table.$dirty && !$v.other_form.table.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Chairs" label-for="chairs" required>
                    <app-input
                      v-model.number="other_form.chairs"
                      type="number"
                      placeholder="123"
                      id="chairs"
                      name="chairs"
                      :error="$v.other_form.chairs.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.chairs.$dirty && !$v.other_form.chairs.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Umbrellas" label-for="umbrellas" required>
                    <app-input
                      v-model.number="other_form.umbrellas"
                      type="number"
                      placeholder="123"
                      id="umbrellas"
                      name="umbrellas"
                      :error="$v.other_form.umbrellas.$error"
                    />
                    <template #error>
                      <div v-if="$v.other_form.umbrellas.$dirty && !$v.other_form.umbrellas.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Wifi: <app-switcher v-model="other_form.wifi" /></app-cells>
                  </app-form-group>
                  <app-form-group v-if="other_form.wifi" label="SSID Password" label-for="ssid_password">
                    <app-input
                      v-model="other_form.password"
                      placeholder="SSID Password"
                      id="ssid_password"
                      name="ssid_password"
                    />
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Books: <app-switcher v-model="other_form.books" /></app-cells>
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Fridge: <app-switcher v-model="other_form.fridge" /></app-cells>
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Hot Pots: <app-switcher v-model="other_form.hot_pots" /></app-cells>
                  </app-form-group>
                  <app-form-group>
                    <app-cells>Coffee bucket: <app-switcher v-model="other_form.coffee_bucket" /></app-cells>
                  </app-form-group>
                </template>
              </app-grid>
              <app-form-group label="Notes" label-for="other_notes">
                <app-textarea
                  v-model="other_form.notes"
                  id="other_notes"
                  placeholder="Notes"
                />
              </app-form-group>
              <app-cells position="center" :indent="false">
                <app-button :disabled="$v.other_form.$error" type="submit" ref="submit_other">Add car</app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
      <modal
        name="other_driver_popup"
        :width="700"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="$modal.hide('other_driver_popup')" class="popup__close" type="button"></button>
          <div class="popup__title">
            <span>Adding taxi driver</span>
          </div>
          <div class="popup__body">
            <form @submit.prevent="onCheckOtherDriverForm">
              <app-grid>
                <template #item-1>
                  <app-form-group label="First name" label-for="first_name" required>
                    <app-input
                      v-model="other_driver_form.first_name"
                      @change.native="$v.other_driver_form.first_name.$touch()"
                      :error="$v.other_driver_form.first_name.$error"
                      id="first_name"
                      placeholder="First name"
                      name="first_name"
                    />
                    <template #error>
                      <div v-if="$v.other_driver_form.first_name.$dirty && !$v.other_driver_form.first_name.required">
                        Required field
                      </div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Middle name" label-for="middle_name">
                    <app-input
                      v-model="other_driver_form.middle_name"
                      id="middle_name"
                      placeholder="Middle name"
                      name="middle_name"
                    />
                  </app-form-group>
                  <app-form-group label="Last name" label-for="last_name" required>
                    <app-input
                      v-model="other_driver_form.last_name"
                      @change.native="$v.other_driver_form.last_name.$touch()"
                      :error="$v.other_driver_form.last_name.$error"
                      id="last_name"
                      placeholder="Last name"
                      name="last_name"
                    />
                    <template #error>
                      <div v-if="$v.other_driver_form.last_name.$dirty && !$v.other_driver_form.last_name.required">
                        Required field
                      </div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Phone number">
                    <app-phone
                      v-model="other_driver_form.phone"
                      placeholder="+7 499 113 28 00"
                    />
                  </app-form-group>
                  <app-form-group label="Other communications">
                    <app-cells :indent="false">
                      <app-checkbox v-model="other_driver_form.phone_telegram" label="Telegram" :readonly="!other_driver_form.phone" />
                      <app-checkbox v-model="other_driver_form.phone_whatsapp" label="Whatsapp" :readonly="!other_driver_form.phone" />
                    </app-cells>
                  </app-form-group>
                </template>
                <template #item-2>
                  <app-form-group label="Company">
                    <app-select
                      v-model="other_driver_form.rental_company"
                      :reduce="item => item.id"
                      :options="company_options"
                      placeholder="Company"
                    />
                  </app-form-group>
                  <app-form-group>
                    <app-checkbox
                      v-model="other_driver_form.op_manual_signed"
                      label="Operations manual signed"
                    />
                  </app-form-group>
                  <app-form-group label="Phone number (additional)">
                    <app-phone
                      v-model="other_driver_form.additional_phone"
                      placeholder="+7 499 113 28 00"
                    />
                  </app-form-group>
                  <app-form-group label="Other communications">
                    <app-cells :indent="false">
                      <app-checkbox
                        v-model="other_driver_form.additional_phone_telegram"
                        label="Telegram"
                        :readonly="!other_driver_form.additional_phone"
                      />
                      <app-checkbox
                        v-model="other_driver_form.additional_phone_whatsapp"
                        label="Whatsapp"
                        :readonly="!other_driver_form.additional_phone"
                      />
                    </app-cells>
                  </app-form-group>
                </template>
              </app-grid>
              <app-form-group label="Notes">
                <app-textarea
                  v-model="other_driver_form.notes"
                  rows="5"
                  placeholder="Write a notes"
                />
              </app-form-group>
              <app-cells position="center" :indent="false">
                <app-button :disabled="$v.other_driver_form.$error" type="submit" ref="submit_other_driver">
                  Add driver
                </app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
    </template>
  </div>
</template>

<script>
import {
  getSafariRentalCars,
  postSafariRentalCars,
  getCarManagementCars,
  getEmployeesListSearch,
  getSafariRentalCompany,
  getSafariDrivers,
  getEmployeesList,
  postSafariDriver, getSafariGroupTypes,
} from '@/http'
import { required } from 'vuelidate/lib/validators'
import AppDocumentIcon from '@/components/AppDocumentIcon.vue'
import { format, parse } from 'date-fns'

export default {
  name: 'SafariCarsDriversList',
  components: { AppDocumentIcon },
  data() {
    return {
      is_loaded: false,
      table_other: {
        items: [],
        columns: [
          'rental_company',
          'car',
          'located',
          'seats',
          'year',
          'car_type',
          'seats_movable',
          'front_a_c',
          'rear_a_c',
          'wifi',
          'fridge',
          'hot_pots',
          'binocular',
          'table',
          'chairs',
          'umbrellas',
          'notes',
        ],
        options: {
          headings: {
            'rental_company': 'Company',
            'car': 'Car',
            'located': 'Located',
            'seats': 'Seats',
            'year': 'Year',
            'car_type': 'Car type',
            'hot_pots': 'Hot pots',
            'front_a_c': 'Front a/c',
            'rear_a_c': 'Rear a/c',
            'fridge': 'Fridge',
            'wifi': 'WiFi',
            'seats_movable': 'Seats movable',
            'binocular': 'Binocular',
            'table': 'Table',
            'chairs': 'Chairs',
            'umbrellas': 'Umbrellas',
            'notes': 'Notes',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      other_form: {
        hot_pots: false,
        front_a_c: false,
        rear_a_c: false,
        fridge: false,
        wifi: false,
        books: false,
        seats_movable: false,
        medical_kit: false,
        coffee_bucket: false,
        reg_card: false,
      },
      car_options: [],
      drivers_options: [],
      location_options: [],
      car_type_options: [],
      company_options: [],
      safari_drivers_options: [],
      table_altezza: {
        items: [],
        columns: [
          'first_name',
          'age',
          'communication',
          'national_id',
          'profile',
          'warnings',
        ],
        options: {
          headings: {
            'first_name': 'Name',
            'age': 'Age',
            'communication': 'Communication',
            'national_id': 'Documents',
            'profile': 'Profile',
            'warnings': 'Warnings',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      other_driver_form: {},
      table_other_drivers: {
        items: [],
        columns: [
          'first_name',
          'rental_company',
          'phone',
          'additional_phone',
          'op_manual_signed',
          'profile',
        ],
        options: {
          headings: {
            'first_name': 'Name',
            'rental_company': 'Company',
            'phone': 'Main phone',
            'additional_phone': 'Additional phone',
            'op_manual_signed': 'Op. Manual',
            'profile': 'Profile',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      other_filter: 'false',
      other_filters: [
        { value: 'false', label: 'Active' },
        { value: 'true', label: 'Blocked' },
        { value: '', label: 'All' },
      ],
      filter_rental_company: null,
      active_filter: 'true',
      active_filters: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Disabled' },
        { value: '', label: 'All' },
      ],
    }
  },
  validations: {
    other_form: {
      rental_company: { required },
      year: { required },
      located: { required },
      seats: { required },
      car_type: { required },
      umbrellas: { required },
      binocular: { required },
      table: { required },
      chairs: { required },
      plate_number: { required },
    },
    other_driver_form: {
      first_name: { required },
      last_name: { required },
    }
  },
  created() {
    Promise.all([
      getEmployeesList({ position: 'safari_driver', employment_status: 'working' }),
      getSafariDrivers({ block: this.other_filter }),
      getSafariRentalCars({ active: this.active_filter })
    ])
      .then(res => {
        this.table_altezza.items = res[0].data
        this.table_other_drivers.items = res[1].data
        this.table_other.items = res[2].data
        this.is_loaded = true
      })
    getCarManagementCars({ available_for_transfers: false }).then(response => { this.car_options = response.data })
    getEmployeesListSearch({ position: 'safari_driver', employment_status: 'working' })
      .then(res => { this.drivers_options = res.data })
    getSafariGroupTypes({ reservations: true }).then(res => { this.car_type_options = res.data })
    getSafariRentalCompany().then(res => { this.company_options = res.data })
    getSafariDrivers().then(res => { this.safari_drivers_options = res.data })
  },
  methods: {
    onChangeFilter() {
      getSafariDrivers({ block: this.other_filter }).then(res => { this.table_other_drivers.items = res.data })
    },
    fetchOtherDriversItems() {
      getSafariDrivers().then(res => { this.table_other_drivers.items = res.data })
    },
    onCheckOtherDriverForm() {
      this.$v.other_driver_form.$touch()
      if (this.$v.other_driver_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendOtherDriverForm()
      }
    },
    sendOtherDriverForm() {
      this.$refs.submit_other_driver.preload = true
      postSafariDriver(this.other_driver_form)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Driver added',
          })
          this.fetchOtherDriversItems()
          this.$modal.hide('other_driver_popup')
          this.other_driver_form = {}
        })
        .catch(() => { this.$refs.submit_other_driver.preload = false })
    },
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    getUserAvatar(crop_url, gender) {
      if (!crop_url) {
        if (gender && gender === 'male') return require('@/assets/img/temp/avatar-slug.jpg')
        if (gender && gender === 'female') return require('@/assets/img/temp/avatar-slug-female.jpg')
        return require('@/assets/img/temp/avatar-slug.jpg')
      }
      return crop_url
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : ''
    },
    driversTableList(drivers) {
      let result = ''
      for (const key in drivers) {
        result += `${drivers[key].first_name} ${drivers[key].last_name}<br>`
      }
      return result
    },
    wifiInfo(name, pass) {
      if (!name && !pass) return 'No info'
      if (name && !pass) return `SSID Name: ${name}`
      if (name && pass) return `SSID Name: ${name}<br>SSID Password: ${pass}`
    },
    fetchOtherCarsItems() {
      getSafariRentalCars({ rental_company: this.filter_rental_company?.id, active: this.active_filter })
        .then(res => { this.table_other.items = res.data })
    },
    onCheckOtherForm() {
      this.$v.other_form.$touch()
      if (this.$v.other_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendOtherForm()
      }
    },
    sendOtherForm() {
      this.$refs.submit_other.preload = true
      postSafariRentalCars(this.other_form)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Car added',
          })
          this.fetchOtherCarsItems()
          this.$modal.hide('other-modal')
          this.$v.other_form.$reset()
        })
        .catch(() => { this.$refs.submit_other.preload = false })
    },
  }
}
</script>
